import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider, IconButton, TextField } from "@mui/material";
import { Add, Remove, CalendarMonth } from "@mui/icons-material";
import { incrementTicket, decrementTicket } from "../../app/store/slices/ticket/ticketBuying";
import {
    formatEventDate,
    formatEventTime,
    formatDateTime
} from "../../utils/dateFormatter";
import { TICKET_STATUS } from "../../hooks/event-hooks/useEventStatus";
import useAuthNavigation from "../../view/authentication/useAuthNavigation";
import { ShmoozShadowLogo } from "../../assets/images";
import { H3, H4, Body2, Body3, H5 } from "../../themes/typography";
import "../../styles/event/EventTicket.css";

function EventTicket({
    ticketId,
    name = 'Default Name',
    price = 120,
    description = 'Default Description',
    ticketValidFor = [],
    saleStartDateTime = '2025-08-21 13:59',
    saleEndDateTime = '2025-08-21 14:59',
    customerLimit = 10,
    exceedStatus,
    availableQty,
    isGroupTicket,
    groupTicketAttendees,
    saleStatus
}) {
    const { openAuth } = useAuthNavigation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth)

    const [inputValue, setInputValue] = useState(0);
    const [maxValue, setMaxValue] = useState(customerLimit);

    const ticketValidity = ticketValidFor?.event_schedule || []


    /**
     * Decrement the value and also dispatch decrement ticket
     */
    const decrement = useCallback(() => {
        setInputValue((prev) => {
            const newValue = Math.max(prev - 1, 0);
            dispatch(decrementTicket({ ticketId }));
            return newValue;
        });
    }, [ticketId, dispatch]);


    /**
     * Increment input value and dispatch increment ticket
     * Also check that if user logged in only then increment the value as return false
     */
    const increment = useCallback(() => {
        if (!user) {
            openAuth();
            return;
        }

        let limit;

        if (exceedStatus) {
            limit = customerLimit;
        } else {
            limit = availableQty >= customerLimit ? customerLimit : availableQty;
        }

        setMaxValue(limit);

        setInputValue((prev) => {
            const newValue = Math.min(prev + 1, limit);
            dispatch(
                incrementTicket({
                    ticketId,
                    name,
                    price,
                    description,
                    isGroupTicket,
                    groupTicketAttendees,
                    limit
                })
            );
            return newValue;
        });
    }, [ticketId, exceedStatus, availableQty, customerLimit, dispatch, user]);


    /**
     * Function to get ticket max limit message
     */
    const getTicketMaxLimitMessage = useCallback(() => {
        if (inputValue === maxValue) {
            if (maxValue === customerLimit) {
                return `Max Limit : ${maxValue}`;
            } else {
                return `Available Tickets : ${maxValue}`;
            }
        }
        return '';
    }, [inputValue, maxValue, customerLimit]);

    const ticketMaxMessage = getTicketMaxLimitMessage();


    /**
     * Function to show sale schedule message conditionally based on ticket status
     */
    const getSaleScheduleMessage = useCallback(() => {
        switch (saleStatus) {
            case TICKET_STATUS.scheduled:
            case TICKET_STATUS.commingSoon:
                return `Sale starts on ${formatDateTime(saleStartDateTime)}`;
            case TICKET_STATUS.live:
                return `Sale ended on ${formatDateTime(saleEndDateTime)}`;
            case TICKET_STATUS.soldOut:
            case TICKET_STATUS.saleEnded:
                return `Sale has ended`;
            default:
                return null;
        }
    }, [saleStatus, saleStartDateTime, saleEndDateTime]);


    const saleScheduleMessage = getSaleScheduleMessage()


    /**
     * Function to get ticket status message based on sale status
     */
    const getTicketStatus = () => {
        switch (saleStatus) {
            case TICKET_STATUS.commingSoon:
                return `Coming soon`;
            case TICKET_STATUS.live:
                return null;
            case TICKET_STATUS.soldOut:
                return "Sold Out"
            case TICKET_STATUS.saleEnded:
                return `Sale Ended`;
            default:
                return null;
        }
    }

    const ticketStatus = getTicketStatus();

    return (
        <div id="main-event-ticket-container">

            <div className="event-ticket-details">

                <div className="main-ticket-detail-container">

                    <div className="event-ticket-name-and-desc-container">
                        <div className="event-ticket-title">
                            <H4 color="var(--secondary-color)" fontWeight={600}>
                                {name}
                            </H4>
                        </div>

                        <div className="event-ticket-description">
                            <Body2 fontWeight={300}>
                                {description}
                            </Body2>
                        </div>
                    </div>

                    <div className="ticket-validity-and-sale-container">

                        <div className="ticket-validity-dates-container">

                            <div className="ticket-validity-title">
                                <H5 color="var(--secondary-color)">
                                    Ticket valid for
                                </H5>
                            </div>

                            <div className="ticket-validity-dates">

                                {ticketValidity.length > 0 && ticketValidity.map((slot, index) => (
                                    <div className="valid-ticket-date-time-slot" key={index}>
                                        <div className="valid-ticket-calendar-icon">
                                            <CalendarMonth
                                                sx={{ color: 'var(--tertiary-color)', fontSize: '1.125rem' }}
                                            />
                                        </div>

                                        <div className="valid-ticket-date-time-container">
                                            <div className="valid-ticket-date-container">
                                                <Body3 fontWeight={500}>
                                                    {/* Tue, 05 Nov 2024 */}
                                                    {formatEventDate(slot?.date)}
                                                </Body3>
                                            </div>

                                            <div className="valid-ticket-time-section">
                                                <Body3>
                                                    {formatEventTime(slot?.date, slot?.from_time)} - {formatEventTime(slot?.date, slot?.to_time)}
                                                </Body3>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                            </div>

                        </div>

                        <div className="ticket-validity-sales-container">
                            {saleScheduleMessage && <Body3>{saleScheduleMessage}</Body3>}
                            <Body3>|</Body3>
                            <Body3>Entry {groupTicketAttendees} Person</Body3>
                        </div>

                    </div>

                </div>

                <div className="shmooz-logo-on-event-ticket">
                    <img src={ShmoozShadowLogo} alt="Shmooz" />
                </div>

            </div>

            <div className="ticket-cuts-layout-container">

                <div className="ticket-cuts-top-circle"></div>
                <div className="ticket-cuts-center-line"></div>
                <div className="ticket-cuts-bottom-circle"></div>

            </div>

            <div className="event-ticket-purchased-layout">

                <div className="event-ticket-purchase-detail">

                    <div className="ticket-qty-selector-input">
                        {/* {saleStatus !== TICKET_STATUS.live && (
                            <div style={{ textAlign: 'center' }}>
                                <H3
                                    color="var(--secondary-color)"
                                    fontWeight={700}
                                >
                                    {ticketStatus}
                                </H3>
                            </div>
                        )} */}

                        <Body2>Quantity</Body2>
                        <div className="ticket-qty-selector-container">
                            <IconButton
                                sx={ticketQtySelectorBtnStyle}
                                onClick={decrement}
                                disabled={inputValue === 0}
                            >
                                <Remove />
                            </IconButton>

                            <TextField
                                value={inputValue}
                                size="small"
                                sx={ticketQtySelectorInputStyle}
                                inputProps={{ style: { textAlign: "center" } }}
                            />

                            <IconButton
                                sx={ticketQtySelectorBtnStyle}
                                onClick={increment}
                                disabled={inputValue === maxValue}
                            >
                                <Add />
                            </IconButton>
                        </div>

                        <Body2 color="var(--secondary-color)">
                            {ticketMaxMessage}
                        </Body2>
                    </div>

                    {/* <div className="ticket-qty-selector-input">
                        {(saleStatus === TICKET_STATUS.live) || exceedStatus ? (
                            <>
                                <Body2>Quantity</Body2>
                                <div className="ticket-qty-selector-container">
                                    <IconButton
                                        sx={ticketQtySelectorBtnStyle}
                                        onClick={decrement}
                                        disabled={inputValue === 0}
                                    >
                                        <Remove />
                                    </IconButton>

                                    <TextField
                                        value={inputValue}
                                        size="small"
                                        sx={ticketQtySelectorInputStyle}
                                        inputProps={{ style: { textAlign: "center" } }}
                                    />

                                    <IconButton
                                        sx={ticketQtySelectorBtnStyle}
                                        onClick={increment}
                                        disabled={inputValue === maxValue}
                                    >
                                        <Add />
                                    </IconButton>
                                </div>

                                <Body2 color="var(--secondary-color)">
                                    {ticketMaxMessage}
                                </Body2>
                            </>
                        ) : (
                            <H3
                                color="var(--secondary-color)"
                                fontWeight={700}
                            >
                                {ticketStatus}
                            </H3>
                        )}
                    </div> */}


                    <Divider
                        sx={{
                            borderColor: 'var(--divider-color)',
                            alignSelf: 'stretch'
                        }}
                    />

                    <div className="ticket-price-container">
                        <Body2>Ticket Price</Body2>

                        <div className="ticket-price-label">
                            {(price === 0 || price === "0.00")
                                ? (
                                    <H4 color="var(--secondary-color)" fontWeight={700}>
                                        Free
                                    </H4>
                                )
                                : (
                                    <>
                                        <H4 color="var(--secondary-color)" fontWeight={700}>{`₹ ${price}`}</H4>
                                    </>
                                )}
                        </div>
                    </div>

                </div>

            </div>

        </div>
    )
}

export default EventTicket

// 200 characters max supports -> ticket description

function TicketNumberInput() {
    return (
        <div className="ticket-qty-selector-input">
            <Body2>Quantity</Body2>
            <div className="ticket-qty-selector-container">
                <IconButton sx={ticketQtySelectorBtnStyle}>
                    <Remove />
                </IconButton>

                <TextField
                    value={0}
                    size="small"
                    sx={ticketQtySelectorInputStyle}
                    inputProps={{ style: { textAlign: "center" } }}
                />

                <IconButton sx={ticketQtySelectorBtnStyle}>
                    <Add />
                </IconButton>
            </div>

            <Body2 color="var(--secondary-color)">
                Max Limit : 2
            </Body2>
        </div>
    );
}

const ticketQtySelectorBtnStyle = {
    width: '32px',
    height: '32px',
    border: "2px solid transparent",
    bgcolor: "var(--quaternary-color)",
    color: "var(--primary-color)",
    "&:hover": {
        bgcolor: "var(--secondary-color)",
        color: "var(--quaternary-color)",
        border: "2px solid var(--quaternary-color)"
    },
    '&.Mui-disabled': {
        bgcolor: "var(--disabled-color)",
        color: "var(--divider-color)",
        border: "2px solid var(--divider-color)"
    }
}

const ticketQtySelectorInputStyle = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        fontWeight: 600,
        fontSize: "1.25rem",
        color: "var(--secondary-color)",
        // textAlign: "center !important",
        width: "3.5rem",
        minWidth: 'auto',
        maxWidth: 'none',
    }
}